import { DateFormat } from '@/constants/DateFormat';
import { Period } from '@/types/period';
import { convertToBusiness, createDate } from './dateUtils';
import { PeriodAbbrevEnum } from '@/constants/PeriodAbbrevEnum';
import { ReturnInterval } from '@/constants/ReturnInterval';
import type { DateTime } from 'luxon';
import type { IPortfolioFactsheetComparisonDataset, IStrategyFactsheetComparisonDataset } from '@/types/IFactsheetData';
import type { StrategyFactsheetResponseDTO } from '@/api-v2/web/strategies';
import type { IPortfolioTreeSubportfolio } from '@/types/IPortfolioTree';

export const sleep = (ms: number): Promise<void> => new Promise((resolve): number => window.setTimeout(resolve, ms));

/**
 * Returns true if:
 * - the period's fromDate is after or equal to the disabledBeforeDate
 * - the period's toDate is before or equal to the disabledAfterDate
 * - the period's fromDate is before the period's toDate
 *
 * Pass in `disabledBeforeDate` or `disabledAfterDate` to use a different date than the global disabledBeforeDate or disabledAfterDate.
 */
export const getIsPeriodValid = ({
  period,
  disabledBeforeDate,
  disabledAfterDate,
}: {
  period: Period;
  disabledBeforeDate: string;
  disabledAfterDate: string;
}): boolean => {
  return (
    period.fromDateString() >= disabledBeforeDate &&
    period.toDateString() <= disabledAfterDate &&
    period.fromDateString() < period.toDateString()
  );
};

export const getPreviousBusinessDay = (): DateTime => {
  return convertToBusiness(createDate().minus({ days: 1 }));
};

export const getPreviousBusinessDayString = (): string => {
  return getPreviousBusinessDay().toFormat(DateFormat.YYYY_MM_DD);
};

export const endDateHsbcFactsheet = convertToBusiness(getPreviousBusinessDay().minus({ months: 1 }).endOf('month'));

/**
 * MAX and LIVE lower bounds use the cutoff date or the next business day after it,
 * consistent with other predefined period start dates. See {@link PeriodAbbrevEnum}.
 */
export const getMaxAndLivePeriods = ({
  endDate,
  disabledBeforeDate,
  liveDate,
}: {
  endDate: DateTime;
  disabledBeforeDate?: string | null;
  liveDate?: string | null;
}): { [key in PeriodAbbrevEnum]?: Period } => {
  const periods: { [key in PeriodAbbrevEnum]?: Period } = {};
  if (disabledBeforeDate) {
    periods[PeriodAbbrevEnum.MAX] = new Period({
      fromDate: createDate(disabledBeforeDate, { add: true }),
      toDate: endDate,
      abbrev: PeriodAbbrevEnum.MAX,
    });
  }

  if (liveDate) {
    periods[PeriodAbbrevEnum.LIVE] = new Period({
      fromDate: createDate(liveDate, { add: true }),
      toDate: endDate,
      abbrev: PeriodAbbrevEnum.LIVE,
    });
  }

  return periods;
};

/**
 * Returns the commonly used periods for a given end date.
 * @param endDate The end date to use for the periods. Defaults to the previous business day.
 * @returns The commonly used periods.
 */
export const getCommonlyUsedPeriods = (endDate = getPreviousBusinessDay()): { [key in PeriodAbbrevEnum]?: Period } => {
  // Other predefined periods relative to the NEXT business day, so we add true to the convertToBusiness function
  const sixMonths = convertToBusiness(endDate.minus({ months: 6 }), true);
  const oneYear = convertToBusiness(endDate.minus({ years: 1 }), true);
  const threeYears = convertToBusiness(endDate.minus({ years: 3 }), true);
  const fiveYears = convertToBusiness(endDate.minus({ years: 5 }), true);
  const tenYears = convertToBusiness(endDate.minus({ years: 10 }), true);

  /**
   * YTD starts from the end of the last year (https://premialab.atlassian.net/browse/WAA-2517)
   */
  const ytd = convertToBusiness(endDate.startOf('year').minus({ days: 1 }));

  return {
    [PeriodAbbrevEnum.SIX_M]: new Period({
      fromDate: sixMonths,
      toDate: endDate,
      abbrev: PeriodAbbrevEnum.SIX_M,
    }),
    [PeriodAbbrevEnum.ONE_Y]: new Period({
      fromDate: oneYear,
      toDate: endDate,
      abbrev: PeriodAbbrevEnum.ONE_Y,
    }),
    [PeriodAbbrevEnum.THREE_Y]: new Period({
      fromDate: threeYears,
      toDate: endDate,
      abbrev: PeriodAbbrevEnum.THREE_Y,
    }),
    [PeriodAbbrevEnum.FIVE_Y]: new Period({
      fromDate: fiveYears,
      toDate: endDate,
      abbrev: PeriodAbbrevEnum.FIVE_Y,
    }),
    [PeriodAbbrevEnum.TEN_Y]: new Period({
      fromDate: tenYears,
      toDate: endDate,
      abbrev: PeriodAbbrevEnum.TEN_Y,
    }),
    [PeriodAbbrevEnum.YTD]: new Period({
      fromDate: ytd,
      toDate: endDate,
      abbrev: PeriodAbbrevEnum.YTD,
    }),
  };
};

/**
 * Returns the uncommon periods for a given end date.
 * @param endDate The end date to use for the periods. Defaults to the previous business day.
 * @returns The uncommon periods.
 */
export const getUncommonPeriods = (endDate = getPreviousBusinessDay()): { [key in PeriodAbbrevEnum]?: Period } => {
  const qtd = convertToBusiness(endDate.startOf('quarter').minus({ days: 1 }));
  const mtd = convertToBusiness(endDate.startOf('month').minus({ days: 1 }));
  const wtd = convertToBusiness(endDate.startOf('week').minus({ days: 1 }));
  const oneDay = convertToBusiness(endDate.minus({ days: 1 }));

  // Other predefined periods relative to the NEXT business day, so we add true to the convertToBusiness function
  const oneMonth = convertToBusiness(endDate.minus({ months: 1 }), true);
  const threeMonths = convertToBusiness(endDate.minus({ months: 3 }), true);

  return {
    [PeriodAbbrevEnum.THREE_M]: new Period({
      fromDate: threeMonths,
      toDate: endDate,
      abbrev: PeriodAbbrevEnum.THREE_M,
    }),
    [PeriodAbbrevEnum.ONE_M]: new Period({
      fromDate: oneMonth,
      toDate: endDate,
      abbrev: PeriodAbbrevEnum.ONE_M,
    }),
    [PeriodAbbrevEnum.MTD]: new Period({
      fromDate: mtd,
      toDate: endDate,
      abbrev: PeriodAbbrevEnum.MTD,
    }),
    [PeriodAbbrevEnum.QTD]: new Period({
      fromDate: qtd,
      toDate: endDate,
      abbrev: PeriodAbbrevEnum.QTD,
    }),
    [PeriodAbbrevEnum.WTD]: new Period({
      fromDate: wtd,
      toDate: endDate,
      abbrev: PeriodAbbrevEnum.WTD,
    }),
    [PeriodAbbrevEnum.ONE_D]: new Period({
      fromDate: oneDay,
      toDate: endDate,
      abbrev: PeriodAbbrevEnum.ONE_D,
    }),
  };
};

/**
 * Returns periods sorted descending by from date
 */
export const sortPeriodsByFromDate = (periods: Period[]): Period[] => {
  return Object.values(periods).sort((a, b): number => {
    const retval = a.fromDate < b.fromDate ? -1 : 1;

    // the from dates will be the same for 1D and WTD at the beginning of the week, but we always want to show 1D last
    if (a.fromDate.equals(b.fromDate)) {
      if (a.abbrev === PeriodAbbrevEnum.ONE_D) {
        return 1;
      }

      if (b.abbrev === PeriodAbbrevEnum.ONE_D) {
        return -1;
      }
    }

    return retval;
  });
};

export const getTimeInterval = (returnInterval: ReturnInterval): 'days' | 'weeks' | 'months' => {
  if (returnInterval === ReturnInterval.MONTHLY) {
    return 'months';
  }

  if (returnInterval === ReturnInterval.WEEKLY) {
    return 'weeks';
  }

  return 'days';
};

export const DEFAULT_PERIOD_PICKER_DISPLAY_PREFS: PeriodAbbrevEnum[] = [
  PeriodAbbrevEnum.MAX,
  PeriodAbbrevEnum.LIVE,
  PeriodAbbrevEnum.TEN_Y,
  PeriodAbbrevEnum.FIVE_Y,
  PeriodAbbrevEnum.THREE_Y,
  PeriodAbbrevEnum.ONE_Y,
  PeriodAbbrevEnum.YTD,
  PeriodAbbrevEnum.SIX_M,
];

const getHistoryStartDate = (
  datum: IStrategyFactsheetComparisonDataset[number] | IPortfolioFactsheetComparisonDataset[number],
): string | null => {
  if ('strategy' in datum) {
    return datum.strategy.historyStartDate ?? null;
  }

  return datum.historyStartDate ?? null;
};

export const getLatestHistoryStartDate = (
  datasets: IStrategyFactsheetComparisonDataset | IPortfolioFactsheetComparisonDataset,
  factsheetData: StrategyFactsheetResponseDTO | IPortfolioTreeSubportfolio | undefined,
): string | undefined => {
  if (!datasets.length || !factsheetData) return;

  let latestStartDate = createDate(getHistoryStartDate(factsheetData));
  for (let i = 1; i < datasets.length; i++) {
    const currentDatum = datasets[i];
    const startDate = createDate(getHistoryStartDate(currentDatum));
    if (startDate > latestStartDate) {
      latestStartDate = startDate;
    }
  }

  return latestStartDate.toFormat(DateFormat.YYYY_MM_DD);
};
